/**
 * Report the device's IANA time zone to the server, for every signed-in user
 * on every platform (web, iOS, Android).
 *
 * users.timezone is NULL (unknown) until something reports it, and the server
 * labels every time it prints from it: "7:00 pm MDT" in the person's zone, or
 * "1:00 am UTC" when unknown. Native push registration also sends it
 * (push-notification.service.ts), but a web user, or a native one who never
 * allowed push, had no other way in, so their session emails were in UTC and
 * read as their own time.
 *
 * When: whenever a user lands in the auth store (sign-in, sign-up, OAuth, the
 * /me on app open) and on resume (visibilitychange, which the Capacitor
 * WebView fires on foreground too). Only when the device zone differs from
 * what the server has, so a page view or an unrelated user update is never a
 * write. The server canonicalises aliases (Asia/Calcutta → Asia/Kolkata), so
 * "what the server has" can legitimately differ from the device's spelling:
 * the last send is remembered (localStorage, per user) with the answer, and a
 * match on it counts as in sync.
 *
 * Several devices, one users.timezone: whichever device reports last wins, and
 * a device sends again whenever the server holds anything but its own zone
 * (c-047). So a stored send also updates the signed-in user in the auth store,
 * rather than the mark remembering the zone the send replaced: "the server
 * still has the zone I replaced" used to count as in sync for good, so once a
 * laptop wrote Denver back, a phone in Tokyo never sent again.
 */
import api from './api';
import { useAuthStore } from '../stores/auth.store';
import { deviceTimeZone } from '../utils/listening';

const MARK_KEY = 'ee:timezone-sync';

/**
 * A refused zone is sent again after this many days. The server's idea of a
 * zone can widen under a device that never moves (c-048: zone names IANA keeps
 * only as links, Europe/Uzhgorod, America/Godthab, were refused and now are
 * not), and such a device would otherwise never ask again.
 */
export const REFUSAL_RETRY_DAYS = 7;
const REFUSAL_RETRY_MS = REFUSAL_RETRY_DAYS * 24 * 60 * 60 * 1000;

type Outcome = 'skipped' | 'sent' | 'failed';

/** The last zone this device reported for a user, and the server's answer. */
interface Mark {
  user: number;
  device: string;
  /** What the server stored (its canonical spelling); null when refused. */
  after: string | null;
  /**
   * When a refusal was saved (ms). A refusal without one predates the dated
   * marks, from a server that refused link zones: it is retried at once.
   */
  refusedAt?: number;
}

/** A refusal mark that has run out: the zone is worth sending again. */
function refusalExpired(mark: Mark): boolean {
  if (mark.after !== null) return false;
  if (typeof mark.refusedAt !== 'number') return true;
  return Date.now() - mark.refusedAt >= REFUSAL_RETRY_MS;
}

let inFlight: string | null = null;

function readMark(): Mark | null {
  try {
    const raw = localStorage.getItem(MARK_KEY);
    return raw ? (JSON.parse(raw) as Mark) : null;
  } catch {
    return null;
  }
}

function writeMark(mark: Mark): void {
  try {
    localStorage.setItem(MARK_KEY, JSON.stringify(mark));
  } catch {
    // Private mode / storage blocked: we may send once more per open, no worse.
  }
}

function inSync(userId: number, device: string, server: string | null): boolean {
  if (server === device) return true;
  const mark = readMark();
  if (mark === null || mark.user !== userId || mark.device !== device) return false;
  // Refused (422): the server did not know this device zone, so asking again
  // on every open would only be refused again; the refusal expires (c-048).
  if (mark.after === null) return !refusalExpired(mark);
  // The server's spelling of this device zone (an alias it canonicalised).
  return server === mark.after;
}

/** Put the stored zone on the user who sent it, if they are still signed in. */
function adopt(userId: number, token: string, stored: string): void {
  const now = useAuthStore.getState();
  if (!now.user || now.user.id !== userId || now.token !== token) return;
  if (now.user.timezone === stored) return;
  useAuthStore.setState({ user: { ...now.user, timezone: stored } });
}

function refused(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 422;
}

/** Send the device zone if the server does not have it. Never throws. */
export async function syncTimezone(): Promise<Outcome> {
  const { user, token } = useAuthStore.getState();
  if (!user || !token) return 'skipped';
  const device = deviceTimeZone();
  if (!device) return 'skipped';
  const server = user.timezone ?? null;
  if (inSync(user.id, device, server)) return 'skipped';

  const key = `${user.id}|${device}`;
  if (inFlight === key) return 'skipped';
  inFlight = key;
  try {
    const response = await api.put('/me/timezone', { timezone: device });
    const stored = (response?.data?.data?.timezone as string | undefined) ?? device;
    writeMark({ user: user.id, device, after: stored });
    adopt(user.id, token, stored);
    return 'sent';
  } catch (error) {
    // A zone the server does not recognise would be refused again on every
    // open; remember it, dated, for REFUSAL_RETRY_DAYS. Anything else
    // (offline, 5xx) is retried next time.
    if (refused(error)) writeMark({ user: user.id, device, after: null, refusedAt: Date.now() });
    return 'failed';
  } finally {
    if (inFlight === key) inFlight = null;
  }
}

/** Wire the triggers once, at app start. Returns the teardown (tests). */
export function installTimezoneSync(): () => void {
  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (state.user && state.user !== prev.user) void syncTimezone();
  });
  const onVisibility = () => {
    if (document.visibilityState === 'visible') void syncTimezone();
  };
  document.addEventListener('visibilitychange', onVisibility);
  void syncTimezone();

  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

/** Cold-start the module's memory (tests only; localStorage is the caller's). */
export function resetTimezoneSyncForTests(): void {
  inFlight = null;
}
